import { appointmentsService } from '@/services/appointments.service';

/**
 * Ask whether to text the patient that their appointment moved, and send it.
 *
 * Called ONLY after the reschedule has already been saved, from both places an
 * appointment's time can change: the appointment modal's Save, and dragging a
 * card to a new slot on the schedule. The move is committed either way — this
 * is a follow-up question, never a gate on the save, so a "no" (or a failed
 * send) leaves a correctly rescheduled appointment behind.
 *
 * Whether to ask at all is decided server-side and arrives as
 * meta.workflow.promptBookedSmsUpdate on the update response: the start time
 * must actually have changed and the account must have "text the patient when
 * staff book" switched on. Callers check that flag; this helper does the asking.
 *
 * Shared so the two call sites cannot drift into asking different questions or
 * reporting a refusal differently.
 */

/**
 * The slice of useToast() this needs. Taken as a parameter rather than calling
 * the hook, so this stays a plain function usable from inside a callback.
 */
export interface BookedSmsUpdateToast {
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

export const BOOKED_SMS_UPDATE_PROMPT =
  'The appointment time has changed. Send the patient an updated text message?';

/**
 * @param appointmentId The appointment id from the SAVE RESPONSE, not the id
 *                      the caller started with. An edit is audit-versioned, so
 *                      saving inserts a new row and the previous id now points
 *                      at a superseded version the sender refuses.
 */
export async function promptAndSendBookedSmsUpdate(
  appointmentId: number,
  toast: BookedSmsUpdateToast,
): Promise<void> {
  if (!window.confirm(BOOKED_SMS_UPDATE_PROMPT)) {
    return;
  }

  try {
    const result = await appointmentsService.sendBookedSmsUpdate(appointmentId);

    if (result.sent) {
      toast.success('Updated text message sent');
      return;
    }

    // A refusal is a normal outcome, not a failure — the appointment is saved.
    // Reported so staff know the patient was NOT told, rather than assuming.
    toast.error(
      'Text not sent',
      'The patient may not be opted in to text messages, or has no valid mobile number.',
    );
  } catch (err) {
    toast.error(
      'Failed to send the updated text message',
      err instanceof Error ? err.message : undefined,
    );
  }
}
